import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm, useWatch } from 'react-hook-form';
import { Row, Col, Form } from 'react-bootstrap';

import { usePrevious } from '_hooks';
import { theme } from '_constants/theme';
import {
  EXCLUDE_ANNUAL_COMPLIANCE_REPORT_STATES,
  FIELDS_KEYS,
} from '_constants/forms';

import { getFieldProps } from '_helpers/forms/fields';

import * as Layout from '_components/atomic/layouts';
import BaseField from '_components/atomic/fields/BaseField';
import SelectField from '_components/atomic/fields/SelectField';
import Button from '_components/atomic/Button';

const StyledForm = styled(Form)`
  width: 100%;
`;

const StyledContainer = styled.div`
  padding: ${theme.spaces.large}px;

  border: 2px solid ${theme.colors.indigo[600]};
  border-radius: 20px;
  background: ${theme.colors.white};

  button {
    justify-content: center;
    width: 100%;
  }

  @media ${theme.devices.tablet} {
    padding: ${theme.spaces.regular}px;
  }
`;

const StyledCol = styled(Col)`
  @media ${theme.devices.tablet} {
    > * {
      margin-bottom: ${theme.spaces.regular}px;
    }
  }
`;

const CheckAvailabilityForm = ({
  geolocation,
  submitDisabled,
  defaultValues,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors, isSubmitted, isSubmitSuccessful, submitCount },
    setValue,
    reset,
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });
  const values = useWatch({ control });
  const prevValues = usePrevious(values);

  const [validationServiceError, setValidationServiceError] = useState(null);
  const [tradeNameVerifying, setTradeNameVerifying] = useState(false);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  useEffect(() => {
    if (
      !values[FIELDS_KEYS.organizedState] &&
      geolocation?.region &&
      geolocation?.countryCode === 'US'
    ) {
      setValue(FIELDS_KEYS.organizedState, geolocation.region);
    }
  }, [values, geolocation, setValue]);

  useEffect(() => {
    if (
      prevValues &&
      prevValues[FIELDS_KEYS.organizedState] !==
        values[FIELDS_KEYS.organizedState]
    ) {
      if (
        EXCLUDE_ANNUAL_COMPLIANCE_REPORT_STATES.includes(
          values[FIELDS_KEYS.organizedState]
        )
      ) {
        setValue(FIELDS_KEYS.compliance, false);
      } else {
        setValue(FIELDS_KEYS.compliance, true);
      }
    }
  }, [prevValues, values, setValue]);

  useEffect(() => {
    if (
      prevValues &&
      prevValues[FIELDS_KEYS.tradeName] !== values[FIELDS_KEYS.tradeName]
    ) {
      setTradeNameVerifying(false);
      reset({ ...values });
    }
  }, [prevValues, values, reset]);

  return (
    <StyledForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <StyledContainer>
        <Row>
          <StyledCol xs={12} md={4}>
            <SelectField
              {...getFieldProps(FIELDS_KEYS.organizedState)}
              label={undefined}
              noBottomSpace
              control={control}
            />
          </StyledCol>
          <StyledCol xs={12} md={4}>
            <BaseField
              {...getFieldProps(FIELDS_KEYS.tradeName, {
                validationOptions: {
                  checkCorporationAvailability: Boolean(
                    values[FIELDS_KEYS.organizedState]
                  ),
                  setProcessing: setTradeNameVerifying,
                  setError: setValidationServiceError,
                  values,
                },
              })}
              loading={
                !errors[FIELDS_KEYS.tradeName]?.message &&
                !validationServiceError &&
                tradeNameVerifying
              }
              error={errors[FIELDS_KEYS.tradeName]}
              label={undefined}
              placeholder="Business Name"
              noBottomSpace
              control={control}
            />
          </StyledCol>
          <Col xs={12} md={4}>
            <Button
              type="submit"
              disabled={
                submitDisabled ||
                tradeNameVerifying ||
                Boolean(errors[FIELDS_KEYS.tradeName]?.message)
              }
              color={theme.colors.indigo[600]}
            >
              Check Availability
            </Button>
          </Col>
        </Row>
      </StyledContainer>
      {validationServiceError && (
        <Layout.Card.Text color={theme.colors.orange[600]} bold>
          {validationServiceError?.message}
        </Layout.Card.Text>
      )}
    </StyledForm>
  );
};

CheckAvailabilityForm.propTypes = {
  submitDisabled: PropTypes.bool,
  geolocation: PropTypes.object,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default CheckAvailabilityForm;
